/**
 * load-slot-picker.ts — overlay shown when "Load Game" is picked from
 * the main menu (v2M2). Lists all four slots with their headers; only
 * occupied slots have an active Load button.
 *
 * Mirrors save-slot-picker.ts in structure so the two feel like the
 * same UI. Differences:
 *   - Load action per row instead of Save / Overwrite
 *   - Empty slots show a disabled Load button
 *   - On Load: deserialise the blob → callbacks.onLoaded(state)
 *   - Errors surface inline as a red status line
 */

import type { LoadedGameData } from '../data/loader';
import {
  deserializeGameState,
  SaveLoadError,
} from '../save/deserialize';
import {
  type SaveBlobHeader,
} from '../save/serialize';
import {
  type SaveSlotKey,
  type SaveStorage,
} from '../save/storage';
import type { GameState } from '../state/types';
import { getAudioEngine } from '../audio';

export interface LoadSlotPickerCallbacks {
  storage: SaveStorage;
  data: LoadedGameData;
  /** Called after a successful load with the restored state. */
  onLoaded(state: GameState): void;
  /** Called when the player cancels without loading. */
  onCancel(): void;
}

export interface LoadSlotPickerHandle {
  destroy(): void;
}

const SLOT_DISPLAY_NAME: Readonly<Record<SaveSlotKey, string>> = {
  'slot:1': 'Slot 1',
  'slot:2': 'Slot 2',
  'slot:3': 'Slot 3',
  quicksave: 'Quicksave',
};

function formatRelativeTime(savedAtMs: number): string {
  const deltaSec = Math.max(0, Math.round((Date.now() - savedAtMs) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.round(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function describeOccupiedSlot(header: SaveBlobHeader): string {
  const label = header.label ?? 'Saved';
  return `${label} · Tick ${header.tick} · ${formatRelativeTime(header.savedAt)}`;
}

export function mountLoadSlotPicker(
  parent: HTMLElement,
  callbacks: LoadSlotPickerCallbacks,
): LoadSlotPickerHandle {
  const overlay = document.createElement('div');
  overlay.id = 'load-slot-picker-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1100',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '480px',
    maxWidth: '600px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'LOAD GAME';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  const errorLine = document.createElement('div');
  Object.assign(errorLine.style, {
    color: '#C84A4A',
    fontSize: '12px',
    minHeight: '16px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(errorLine);

  function performLoad(slot: SaveSlotKey): void {
    errorLine.textContent = '';
    const blob = callbacks.storage.read(slot);
    if (blob === null) {
      errorLine.textContent =
        'Save file could not be read — it may be corrupted or empty.';
      return;
    }
    try {
      const state = deserializeGameState(blob, callbacks.data);
      callbacks.onLoaded(state);
    } catch (err) {
      const msg =
        err instanceof SaveLoadError
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
      errorLine.textContent = msg;
    }
  }

  function makeSlotRow(slot: SaveSlotKey, header: SaveBlobHeader | null): HTMLElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      background: '#05060A',
      border: '1px solid #2A3344',
      borderRadius: '2px',
    } as Partial<CSSStyleDeclaration>);

    const left = document.createElement('div');
    Object.assign(left.style, {
      flex: '0 0 100px',
      fontWeight: '600',
      fontSize: '13px',
      letterSpacing: '0.06em',
    } as Partial<CSSStyleDeclaration>);
    left.textContent = SLOT_DISPLAY_NAME[slot];
    row.appendChild(left);

    const middle = document.createElement('div');
    Object.assign(middle.style, {
      flex: '1 1 auto',
      fontSize: '12px',
      color: header ? '#D7DEEA' : '#7E8799',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    } as Partial<CSSStyleDeclaration>);
    if (header) {
      const badge = makeOutcomeBadge(header.gameOutcome);
      if (badge) middle.appendChild(badge);
      const desc = document.createElement('span');
      desc.textContent = describeOccupiedSlot(header);
      middle.appendChild(desc);
    } else {
      middle.textContent = 'Empty';
    }
    row.appendChild(middle);

    const action = document.createElement('button');
    action.type = 'button';
    action.textContent = 'Load';
    const enabled = header !== null;
    Object.assign(action.style, {
      flex: '0 0 auto',
      padding: '8px 16px',
      background: enabled ? '#2B4A7A' : '#0E131C',
      color: enabled ? '#FFFFFF' : '#505868',
      border: `1px solid ${enabled ? '#3F6FA8' : '#2A3344'}`,
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: enabled ? 'pointer' : 'not-allowed',
    } as Partial<CSSStyleDeclaration>);
    action.disabled = !enabled;
    if (enabled) {
      action.addEventListener('click', () => performLoad(slot));
    }
    row.appendChild(action);

    return row;
  }

  for (const entry of callbacks.storage.listSlots()) {
    card.appendChild(makeSlotRow(entry.key, entry.header));
  }

  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(footer);

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  Object.assign(cancelBtn.style, {
    padding: '10px 20px',
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  cancelBtn.addEventListener('click', callbacks.onCancel);
  footer.appendChild(cancelBtn);

  parent.appendChild(overlay);
  getAudioEngine().playCue('ui:panel-open');
  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.remove();
    },
  };
}

/**
 * v3M12.4 — small status badge for completed-game saves. Returns
 * `null` for in-progress games. Per WIN_CONDITIONS §7: green check
 * for victory, red ✗ for defeat. Mirrors save-slot-picker's helper
 * (could move to a shared file in v3M11 polish if more pickers
 * surface; not worth a refactor for two callers).
 */
function makeOutcomeBadge(
  outcome: 'in-progress' | 'victory' | 'defeat',
): HTMLElement | null {
  if (outcome === 'in-progress') return null;
  const badge = document.createElement('span');
  badge.textContent = outcome === 'victory' ? '✓' : '✗';
  Object.assign(badge.style, {
    color: outcome === 'victory' ? '#63C77C' : '#D24548',
    fontWeight: '700',
    fontSize: '14px',
    flex: '0 0 auto',
  } as Partial<CSSStyleDeclaration>);
  badge.title = outcome === 'victory' ? 'Victory' : 'Defeat';
  return badge;
}
